import request from 'scripts/services/request';
import authStore from 'scripts/auth/authStore';
import { withCredentials, headers } from 'scripts/services/helpers';
import {
  advisorCancelConnection,
  advisorClientAnnualStatementCsv,
  advisorClientAnnualStatementPdf,
  advisorClient,
  advisorClientHistoricalAccountValues,
  advisorClientOrders,
  advisorClientPortfolioStatement,
  advisorClientSmsfTrustDeedDocument,
  advisorClientTaxReport,
  advisorClientWholesaleCertificate,
  advisorClients,
  advisorClientsOverview,
  advisorPendingRequests,
  advisorRequestConnection,
  advisorRevokeConnection,
  advisorSignupClient,
} from 'scripts/services/api';

const OPEN_IN_NEW_TAB_CONTENT_TYPES = ['application/pdf'];

const isDownloadUrl = (value) =>
  typeof value === 'string' && /^(https?:\/\/|\/)/i.test(value);

const extractDownloadUrl = (payload) => {
  if (!payload) return null;
  if (typeof payload === 'string') {
    return isDownloadUrl(payload) ? payload : null;
  }

  const candidate = payload.url || payload.redirectTo || payload.downloadUrl;
  return isDownloadUrl(candidate) ? candidate : null;
};

const extractFilename = (contentDisposition) => {
  if (!contentDisposition) return null;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match && utf8Match[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const quotedMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return quotedMatch && quotedMatch[1] ? quotedMatch[1] : null;
};

const triggerBrowserDownload = ({ href, filename, openInNewTab = false }) => {
  if (!CLIENT) return;

  const anchor = window.document.createElement('a');
  anchor.href = href;
  if (openInNewTab) {
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
  }
  if (filename && !openInNewTab) {
    anchor.download = filename;
  }

  window.document.body.appendChild(anchor);
  anchor.click();
  window.document.body.removeChild(anchor);
};

const getErrorMessage = async (response) => {
  const fallbackMessage = 'Unable to download adviser client document.';

  try {
    const text = await response.text();
    if (!text) return fallbackMessage;

    try {
      const payload = JSON.parse(text);
      return payload.message || payload.error || fallbackMessage;
    } catch (error) {
      return text;
    }
  } catch (error) {
    return fallbackMessage;
  }
};

class AdvisorService {
  getClients() {
    return request(advisorClients(), {
      ...withCredentials,
    });
  }

  getClientsOverview() {
    return request(advisorClientsOverview(), {
      ...withCredentials,
    });
  }

  getClient(clientId) {
    return request(advisorClient(clientId), {
      ...withCredentials,
    });
  }

  getClientOrders(clientId) {
    return request(advisorClientOrders(clientId), {
      ...withCredentials,
    });
  }

  getClientHistoricalAccountValues(clientId) {
    return request(advisorClientHistoricalAccountValues(clientId), {
      ...withCredentials,
    });
  }

  getPendingRequests() {
    return request(advisorPendingRequests(), {
      ...withCredentials,
    });
  }

  requestConnection(clientEmail) {
    return request(advisorRequestConnection(), {
      method: 'POST',
      ...withCredentials,
      headers,
      body: JSON.stringify({ clientEmail }),
    });
  }

  signupClient({ givenName, email, accountType }) {
    const formData = [
      `givenName=${encodeURIComponent(givenName)}`,
      `email=${encodeURIComponent(email)}`,
      `accountType=${encodeURIComponent(accountType)}`,
    ].join('&');

    return request(advisorSignupClient(), {
      method: 'POST',
      ...withCredentials,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      },
      body: formData,
    });
  }

  cancelConnection(clientId) {
    return request(advisorCancelConnection(clientId), {
      method: 'POST',
      ...withCredentials,
    });
  }

  revokeConnection(clientId) {
    return request(advisorRevokeConnection(clientId), {
      method: 'POST',
      ...withCredentials,
    });
  }

  downloadPortfolioStatement(clientId) {
    return this._downloadClientDocument(
      advisorClientPortfolioStatement(clientId),
    );
  }

  downloadSmsfTrustDeed(clientId) {
    return this._downloadClientDocument(
      advisorClientSmsfTrustDeedDocument(clientId),
    );
  }

  downloadWholesaleCertificate(clientId, options) {
    return this._downloadClientDocument(
      advisorClientWholesaleCertificate(clientId),
      options,
    );
  }

  downloadAnnualStatementPdf(clientId, year) {
    return this._downloadClientDocument(
      advisorClientAnnualStatementPdf(clientId, year),
    );
  }

  downloadAnnualStatementCsv(clientId, year) {
    return this._downloadClientDocument(
      advisorClientAnnualStatementCsv(clientId, year),
      { filename: `annual-statement-${year}.csv` },
    );
  }

  downloadTaxReport(clientId, year, options) {
    return this._downloadClientDocument(
      advisorClientTaxReport(clientId, year),
      {
        ...options,
        filename: `tax-report-${year}.pdf`,
        mimeType: 'application/pdf',
      },
    );
  }

  async _downloadClientDocument(
    url,
    { credentials = 'include', filename: defaultFilename, mimeType } = {},
  ) {
    const response = await fetch(url, {
      credentials,
      headers: {
        Authorization: authStore.getAuthToken(),
      },
    });

    if (!response.ok) {
      throw new Error(await getErrorMessage(response));
    }

    const contentType = response.headers.get('content-type') || '';
    const contentDisposition = response.headers.get('content-disposition');
    const isTextResponse =
      contentType.includes('application/json') ||
      contentType.startsWith('text/plain');

    if (isTextResponse) {
      const text = await response.clone().text();
      if (text) {
        try {
          const payload = JSON.parse(text);
          const downloadUrl = extractDownloadUrl(payload);
          if (downloadUrl) {
            triggerBrowserDownload({ href: downloadUrl, openInNewTab: true });
            return;
          }
        } catch (error) {
          const downloadUrl = extractDownloadUrl(text);
          if (downloadUrl) {
            triggerBrowserDownload({ href: downloadUrl, openInNewTab: true });
            return;
          }
        }
      }
    }

    const blob = await response.blob();
    const downloadBlob = mimeType ? blob.slice(0, blob.size, mimeType) : blob;
    const blobUrl = window.URL.createObjectURL(downloadBlob);
    const redirectedFilename =
      credentials === 'omit' && response.redirected
        ? decodeURIComponent(new URL(response.url).pathname.split('/').pop())
        : null;
    const filename =
      extractFilename(contentDisposition) ||
      defaultFilename ||
      redirectedFilename;
    const openInNewTab = OPEN_IN_NEW_TAB_CONTENT_TYPES.some((type) =>
      (mimeType || contentType).includes(type),
    );

    triggerBrowserDownload({
      href: blobUrl,
      filename,
      openInNewTab,
    });

    window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
  }
}

export default new AdvisorService();
